import { type FormEvent, useEffect, useState } from 'react';
import { useSWRConfig } from 'swr';
import {
  Button,
  Card,
  ColorField,
  ErrorNote,
  Field,
  Icon,
  type IconName,
  Select,
  TextInput,
  Textarea,
  cx,
} from '../components/ui/index.js';
import { ApiError, apiRequest, getApiBaseUrl } from '../lib/api.js';
import { readJson } from '../lib/remote.js';
import {
  SETUP_DRAFT_KEY,
  SETUP_STEPS,
  canonicalOrigin,
  eurosToCents,
  loadSetupDraft,
  planCodeFromName,
  readWizardCompleted,
  type SetupDraft,
  type SetupStepId,
} from '../lib/setup-wizard.js';
import {
  TenantBillingFields,
  emptyTenantBilling,
  parseTenantBilling,
  type TenantBillingValues,
} from '../components/TenantBillingFields.js';
import { timezoneChoices } from '../lib/timezones.js';
import { refreshAdminUiPrefs } from '../lib/ui-prefs.js';
import { useAdminLocale } from '../lib/admin-locale.js';

interface PlatformSettings {
  provider: { kind: 'mock' | 'openai'; model: string | null; keyConfigured: boolean };
  smtp: {
    host: string | null;
    port: number | null;
    user: string | null;
    mailFrom: string | null;
    passwordSet: boolean;
    source: 'platform' | 'env' | 'none';
  };
}

interface SettingItem {
  key: string;
  value: string;
}

interface TenantRow {
  id: string;
  name: string;
  timezone: string;
}

interface PlanRow {
  id: string;
  code: string;
  name: string;
  monthlyPriceCents: number;
}

interface SiteRow {
  id: string;
  name: string;
  canonicalOrigin: string;
  status: string;
}

interface SubscriptionView {
  plan: { code: string; name: string };
}

interface BotLocale {
  locale: 'el' | 'en' | 'fr';
  enabled: boolean;
  greeting: string;
  fallback: string;
}

interface BotView {
  defaultLocale: 'el' | 'en' | 'fr';
  locales: BotLocale[];
}

interface InstallationRow {
  status: string;
}

type LocalText = (el: string, en: string) => string;

function getStepMeta(text: LocalText): Record<SetupStepId, { label: string; icon: IconName }> {
  return {
    welcome: { label: text('Καλωσόρισμα', 'Welcome'), icon: 'home' },
    brand: { label: text('Εμφάνιση', 'Appearance'), icon: 'eye' },
    ai: { label: text('Πάροχος AI', 'AI provider'), icon: 'pulse' },
    mail: { label: 'Email', icon: 'mail' },
    customer: { label: text('Πελάτης', 'Customer'), icon: 'building' },
    site: { label: text('Ιστοσελίδα', 'Website'), icon: 'globe' },
    knowledge: { label: text('Γνώση', 'Knowledge'), icon: 'file' },
    connect: { label: 'WordPress', icon: 'link' },
    invite: { label: text('Πρόσκληση', 'Invitation'), icon: 'users' },
    done: { label: text('Έτοιμο', 'Done'), icon: 'check' },
  };
}

function explain(error: unknown, fallback: string, text: LocalText): string {
  const message = error instanceof ApiError ? error.message : '';
  if (message.includes('production'))
    return text(
      'Σε production ο δοκιμαστικός πάροχος δεν επιτρέπεται.',
      'The mock provider is not allowed in production.',
    );
  if (message.includes('key or chat model')) {
    return text(
      'Ο διακομιστής δεν έχει κλειδί ή μοντέλο OpenAI. Το κλειδί δεν γράφεται σε αυτή τη φόρμα.',
      'The server has no OpenAI key or model. The key is not entered in this form.',
    );
  }
  if (message.includes('already registered'))
    return text('Αυτό το origin χρησιμοποιείται ήδη.', 'This origin is already in use.');
  if (message.includes('active subscription')) {
    return text(
      'Ο πελάτης χρειάζεται ενεργό πακέτο πριν δημιουργηθεί ιστοσελίδα.',
      'The customer needs an active plan before a website can be created.',
    );
  }
  if (message.includes('site limit'))
    return text('Το πακέτο δεν χωράει άλλη ιστοσελίδα.', 'The plan has reached its website limit.');
  if (message.includes('plugin package')) {
    return text(
      'Το ZIP του plugin δεν έχει χτιστεί. Τρέξτε pnpm plugin:package στον διακομιστή.',
      'The plugin ZIP has not been built. Run pnpm plugin:package on the server.',
    );
  }
  if (message.includes('http origins'))
    return text(
      'Τα http origins επιτρέπονται μόνο σε τοπική ανάπτυξη.',
      'HTTP origins are allowed only in local development.',
    );
  return message || fallback;
}

function originIssue(value: string, text: LocalText): string | null {
  const trimmed = value.trim();
  if (!trimmed) return text('Συμπληρώστε το origin της ιστοσελίδας.', 'Enter the website origin.');
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return text(
      'Χρειάζεται πλήρες URL, για παράδειγμα https://www.example.com.',
      'Enter a full URL, for example https://www.example.com.',
    );
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:')
    return text('Το origin δέχεται μόνο http ή https.', 'The origin accepts only HTTP or HTTPS.');
  if (url.username || url.password || url.search || url.hash)
    return text(
      'Χωρίς κωδικό πρόσβασης, query ή fragment.',
      'Do not include credentials, a query, or a fragment.',
    );
  if (url.pathname !== '/' && url.pathname !== '')
    return text(
      'Χωρίς διαδρομή. Μόνο το origin, για παράδειγμα https://www.example.com.',
      'Do not include a path. Use only the origin, for example https://www.example.com.',
    );
  if (url.hostname.includes('*'))
    return text('Δεν επιτρέπεται wildcard.', 'Wildcards are not allowed.');
  return null;
}

function settingValue(items: SettingItem[], key: string, fallback: string): string {
  return items.find((item) => item.key === key)?.value || fallback;
}

function saveDraft(draft: SetupDraft): void {
  try {
    window.sessionStorage.setItem(SETUP_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // The wizard still works for this page view.
  }
}

function formatMoney(cents: number, locale: 'el' | 'en'): string {
  return new Intl.NumberFormat(locale === 'en' ? 'en-GB' : 'el-GR', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

export function SetupWizardPage(props: {
  onLater: () => void;
  onFinished: (tenantId: string) => void;
}) {
  const { locale, text } = useAdminLocale();
  const { mutate } = useSWRConfig();
  const initial = loadSetupDraft(typeof window === 'undefined' ? null : window.sessionStorage);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [alreadyDone, setAlreadyDone] = useState(false);
  const [step, setStep] = useState(initial.step);
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [providerKind, setProviderKind] = useState<'mock' | 'openai'>('mock');
  const [providerReady, setProviderReady] = useState(false);
  const [providerModel, setProviderModel] = useState<string | null>(null);
  const [smtpSource, setSmtpSource] = useState<'platform' | 'env' | 'none'>('none');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpFrom, setSmtpFrom] = useState('');

  const [productName, setProductName] = useState('UNIBYTE AI');
  const [accent, setAccent] = useState('#2563EB');

  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [customerMode, setCustomerMode] = useState<'new' | 'existing'>('new');
  const [tenantName, setTenantName] = useState('');
  const [timezone, setTimezone] = useState('Europe/Athens');
  const [billing, setBilling] = useState<TenantBillingValues>(emptyTenantBilling);
  const [tenantId, setTenantId] = useState(initial.tenantId);
  const [planMode, setPlanMode] = useState<'existing' | 'new'>('existing');
  const [planCode, setPlanCode] = useState('');
  const [newPlanName, setNewPlanName] = useState('Starter');
  const [newPlanCode, setNewPlanCode] = useState('starter');
  const [priceEuros, setPriceEuros] = useState('0');
  const [activePlanCode, setActivePlanCode] = useState<string | null>(null);

  const [sites, setSites] = useState<SiteRow[]>([]);
  const [siteMode, setSiteMode] = useState<'new' | 'existing'>('new');
  const [siteName, setSiteName] = useState('');
  const [origin, setOrigin] = useState('https://');
  const [siteId, setSiteId] = useState(initial.siteId);
  const [greeting, setGreeting] = useState('');

  const [knowledgeTitle, setKnowledgeTitle] = useState(() =>
    text('Πρώτες πληροφορίες', 'Initial information'),
  );
  const [knowledgeText, setKnowledgeText] = useState('');
  const [knowledgeReady, setKnowledgeReady] = useState(false);

  const [pairingCode, setPairingCode] = useState(initial.pairingCode);
  const [pairingExpiresAt, setPairingExpiresAt] = useState(initial.pairingExpiresAt);
  const [pairingSiteId, setPairingSiteId] = useState(initial.pairingSiteId);
  const [connected, setConnected] = useState(false);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSent, setInviteSent] = useState(false);

  const current = SETUP_STEPS[step] ?? 'welcome';
  const stepMeta = getStepMeta(text);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const [settings, application, tenantRows, planRows] = await Promise.all([
          readJson<PlatformSettings>('/v1/platform/settings', undefined, null),
          readJson<{ items: SettingItem[] }>('/v1/platform/application-settings', undefined, null),
          readJson<TenantRow[]>('/v1/tenants', undefined, null),
          readJson<PlanRow[]>('/v1/plans', undefined, null),
        ]);
        if (!active) return;
        setProviderKind(settings.provider.kind);
        setProviderReady(settings.provider.keyConfigured && Boolean(settings.provider.model));
        setProviderModel(settings.provider.model);
        setSmtpSource(settings.smtp.source);
        setSmtpHost(settings.smtp.host ?? '');
        setSmtpPort(String(settings.smtp.port ?? 587));
        setSmtpUser(settings.smtp.user ?? '');
        setSmtpFrom(settings.smtp.mailFrom ?? '');
        setProductName(settingValue(application.items, 'admin_product_name', 'UNIBYTE AI'));
        setAccent(settingValue(application.items, 'admin_accent_color', '#2563EB'));
        setTimezone(settingValue(application.items, 'default_timezone', 'Europe/Athens'));
        setTenants(tenantRows);
        setPlans(planRows);
        setPlanCode(planRows[0]?.code ?? '');
        setAlreadyDone(readWizardCompleted(application.items) === true && initial.step === 0);
        if (tenantRows.length === 0) setCustomerMode('new');
      } catch (caught) {
        if (active)
          setLoadError(
            explain(
              caught,
              text('Οι ρυθμίσεις δεν φορτώθηκαν.', 'The settings could not be loaded.'),
              text,
            ),
          );
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [initial.step]);

  useEffect(() => {
    if (!tenantId) return;
    let active = true;
    void (async () => {
      try {
        const [siteRows, subscription] = await Promise.all([
          readJson<SiteRow[]>(`/v1/tenants/${tenantId}/sites`, undefined, null),
          readJson<SubscriptionView>('/v1/subscription', undefined, tenantId).catch(
            (caught: unknown) => {
              if (caught instanceof ApiError && caught.status === 404) return null;
              throw caught;
            },
          ),
        ]);
        if (!active) return;
        setSites(siteRows);
        setActivePlanCode(subscription?.plan.code ?? null);
        if (siteRows.length === 0) setSiteMode('new');
      } catch (caught) {
        if (active)
          setError(
            explain(
              caught,
              text(
                'Τα στοιχεία του πελάτη δεν φορτώθηκαν.',
                'The customer details could not be loaded.',
              ),
              text,
            ),
          );
      }
    })();
    return () => {
      active = false;
    };
  }, [tenantId, text]);

  function remember(patch: Partial<SetupDraft>, nextStep = step): void {
    const draft: SetupDraft = {
      step: nextStep,
      tenantId,
      siteId,
      pairingCode,
      pairingExpiresAt,
      pairingSiteId,
      ...patch,
    };
    saveDraft(draft);
  }

  function go(next: number, patch?: Partial<SetupDraft>): void {
    setError(null);
    setNotice(null);
    setStep(next);
    remember({ ...patch, step: next }, next);
  }

  async function saveBrand(event: FormEvent): Promise<void> {
    event.preventDefault();
    const name = productName.trim();
    if (!name) {
      setError(text('Συμπληρώστε το όνομα του προϊόντος.', 'Enter the product name.'));
      return;
    }
    setPending(true);
    setError(null);
    try {
      await readJson(
        '/v1/platform/application-settings',
        {
          method: 'PATCH',
          body: JSON.stringify({
            items: [
              { key: 'admin_product_name', value: name },
              { key: 'admin_accent_color', value: accent },
            ],
          }),
        },
        null,
      );
      refreshAdminUiPrefs();
      go(step + 1);
    } catch (caught) {
      setError(
        explain(
          caught,
          text('Η εμφάνιση δεν αποθηκεύτηκε.', 'The appearance settings were not saved.'),
          text,
        ),
      );
    } finally {
      setPending(false);
    }
  }

  async function saveProvider(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (providerKind === 'openai' && !providerReady) {
      setError(
        text(
          'Η επιλογή OpenAI μένει ανενεργή ώσπου ο διακομιστής να έχει κλειδί και μοντέλο.',
          'OpenAI remains unavailable until the server has a key and model.',
        ),
      );
      return;
    }
    setPending(true);
    setError(null);
    try {
      await readJson(
        '/v1/platform/settings',
        { method: 'PATCH', body: JSON.stringify({ provider: providerKind }) },
        null,
      );
      go(step + 1);
    } catch (caught) {
      setError(
        explain(caught, text('Ο πάροχος δεν αποθηκεύτηκε.', 'The provider was not saved.'), text),
      );
    } finally {
      setPending(false);
    }
  }

  async function saveSmtp(event: FormEvent): Promise<void> {
    event.preventDefault();
    const port = Number.parseInt(smtpPort, 10);
    if (!smtpHost.trim() || !smtpFrom.trim() || !Number.isInteger(port)) {
      setError(
        text(
          'Συμπληρώστε host, θύρα και αποστολέα, ή παραλείψτε το email.',
          'Enter the host, port, and sender, or skip email setup.',
        ),
      );
      return;
    }
    setPending(true);
    setError(null);
    try {
      await readJson(
        '/v1/platform/settings/smtp',
        {
          method: 'PATCH',
          body: JSON.stringify({
            host: smtpHost.trim(),
            port,
            user: smtpUser.trim() || null,
            mailFrom: smtpFrom.trim(),
            ...(smtpPassword ? { password: smtpPassword } : {}),
          }),
        },
        null,
      );
      setSmtpPassword('');
      setSmtpSource('platform');
      go(step + 1);
    } catch (caught) {
      setError(explain(caught, text('Το SMTP δεν αποθηκεύτηκε.', 'SMTP was not saved.'), text));
    } finally {
      setPending(false);
    }
  }

  async function saveCustomer(event: FormEvent): Promise<void> {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      let nextTenant = tenantId;
      let createdNew = false;
      if (customerMode === 'new') {
        const name = tenantName.trim();
        if (!name)
          throw new ApiError(
            400,
            text('Συμπληρώστε την επωνυμία του πελάτη.', 'Enter the customer name.'),
          );
        const profile = parseTenantBilling(billing);
        if (!profile.ok)
          throw new ApiError(400, locale === 'en' ? 'Check the billing details.' : profile.message);
        const created = await readJson<TenantRow>(
          '/v1/tenants',
          { method: 'POST', body: JSON.stringify({ name, timezone, ...profile.value }) },
          null,
        );
        nextTenant = created.id;
        createdNew = true;
        setTenants((currentRows) => [...currentRows, created]);
        setTenantId(created.id);
        setCustomerMode('existing');
      } else if (!nextTenant) {
        throw new ApiError(400, text('Επιλέξτε πελάτη.', 'Select a customer.'));
      }

      let code = planCode;
      if (planMode === 'new') {
        const cents = eurosToCents(priceEuros);
        const slug = newPlanCode.trim().toLowerCase();
        if (!newPlanName.trim() || !/^[a-z0-9-]+$/.test(slug)) {
          throw new ApiError(
            400,
            text(
              'Το πακέτο χρειάζεται όνομα και κωδικό με πεζά λατινικά, αριθμούς ή παύλα.',
              'The plan needs a name and a code using lowercase Latin letters, numbers, or hyphens.',
            ),
          );
        }
        if (cents === null)
          throw new ApiError(
            400,
            text(
              'Η τιμή πρέπει να είναι ποσό σε ευρώ, π.χ. 29 ή 29,90.',
              'The price must be an amount in euros, for example 29 or 29.90.',
            ),
          );
        const created = await readJson<PlanRow>(
          '/v1/plans',
          {
            method: 'POST',
            body: JSON.stringify({
              code: slug,
              name: newPlanName.trim(),
              monthlyPriceCents: cents,
              currency: 'EUR',
              limits: {
                sites: 2,
                monthlyAnswers: 1000,
                maxSources: 500,
                maxPdfBytes: 10 * 1024 * 1024,
                maxExtractedChars: 2_000_000,
                maxInputTokens: 8_000,
                maxOutputTokens: 400,
              },
            }),
          },
          null,
        );
        setPlans((currentRows) => [...currentRows, created]);
        code = created.code;
        setPlanCode(created.code);
        setPlanMode('existing');
      }
      if (!code) throw new ApiError(400, text('Επιλέξτε πακέτο.', 'Select a plan.'));

      let currentCode: string | null = null;
      if (!createdNew) {
        const subscription = await readJson<SubscriptionView>(
          '/v1/subscription',
          undefined,
          nextTenant,
        ).catch((caught: unknown) => {
          if (caught instanceof ApiError && caught.status === 404) return null;
          throw caught;
        });
        currentCode = subscription?.plan.code ?? null;
      }
      if (currentCode !== code) {
        await readJson(
          `/v1/tenants/${nextTenant}/subscription`,
          { method: 'POST', body: JSON.stringify({ planCode: code }) },
          null,
        );
        setActivePlanCode(code);
      }
      go(step + 1, { tenantId: nextTenant });
    } catch (caught) {
      setError(
        explain(caught, text('Ο πελάτης δεν ετοιμάστηκε.', 'The customer was not set up.'), text),
      );
    } finally {
      setPending(false);
    }
  }

  async function saveSite(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!tenantId) {
      setError(
        text(
          'Λείπει ο πελάτης. Γυρίστε στο προηγούμενο βήμα.',
          'The customer is missing. Return to the previous step.',
        ),
      );
      return;
    }
    const problem = siteMode === 'new' ? originIssue(origin, text) : null;
    if (problem) {
      setError(problem);
      return;
    }
    setPending(true);
    setError(null);
    try {
      let nextSite = siteId;
      if (siteMode === 'new') {
        const name = siteName.trim();
        if (!name)
          throw new ApiError(
            400,
            text('Συμπληρώστε το όνομα της ιστοσελίδας.', 'Enter the website name.'),
          );
        const created = await readJson<SiteRow>(
          `/v1/tenants/${tenantId}/sites`,
          {
            method: 'POST',
            body: JSON.stringify({
              name,
              canonicalOrigin: canonicalOrigin(origin),
              timezone,
            }),
          },
          null,
        );
        nextSite = created.id;
        setSites((currentRows) => [...currentRows, created]);
        setSiteId(created.id);
        setSiteMode('existing');
      } else if (!nextSite) {
        throw new ApiError(400, text('Επιλέξτε ιστοσελίδα.', 'Select a website.'));
      }

      const bot = await readJson<BotView>(`/v1/sites/${nextSite}/bot`, undefined, tenantId);
      const greetingText = greeting.trim();
      const locales =
        greetingText && bot.locales.length > 0
          ? bot.locales.map((botLocale) => ({
              locale: botLocale.locale,
              enabled: botLocale.enabled,
              greeting: botLocale.locale === bot.defaultLocale ? greetingText : botLocale.greeting,
              fallback: botLocale.fallback,
            }))
          : undefined;
      await readJson(
        `/v1/sites/${nextSite}/bot`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            enabled: true,
            ...(locales ? { locales } : {}),
          }),
        },
        tenantId,
      );
      go(step + 1, { tenantId, siteId: nextSite });
    } catch (caught) {
      setError(
        explain(caught, text('Η ιστοσελίδα δεν ετοιμάστηκε.', 'The website was not set up.'), text),
      );
    } finally {
      setPending(false);
    }
  }

  async function saveKnowledge(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!tenantId || !siteId) {
      setError(
        text(
          'Λείπει η ιστοσελίδα. Γυρίστε στο προηγούμενο βήμα.',
          'The website is missing. Return to the previous step.',
        ),
      );
      return;
    }
    const title = knowledgeTitle.trim();
    const knowledgeValue = knowledgeText.trim();
    if (!title || !knowledgeValue) {
      setError(
        text(
          'Συμπληρώστε τίτλο και κείμενο, ή παραλείψτε τη γνώση.',
          'Enter a title and text, or skip knowledge setup.',
        ),
      );
      return;
    }
    setPending(true);
    setError(null);
    try {
      const created = await readJson<{ id: string }>(
        `/v1/sites/${siteId}/sources`,
        {
          method: 'POST',
          body: JSON.stringify({ type: 'TEXT', title, text: knowledgeValue, locale: 'el' }),
        },
        tenantId,
      );
      try {
        await readJson(
          `/v1/sites/${siteId}/sources/${created.id}/activate`,
          { method: 'POST' },
          tenantId,
        );
        setKnowledgeReady(true);
        setNotice(null);
      } catch {
        setKnowledgeReady(false);
        setNotice(
          text(
            'Η πηγή αποθηκεύτηκε ως πρόχειρη. Ενεργοποιήστε την από τη Βάση γνώσης.',
            'The source was saved as a draft. Activate it from the Knowledge Base.',
          ),
        );
      }
      go(step + 1);
    } catch (caught) {
      setError(
        explain(caught, text('Η γνώση δεν αποθηκεύτηκε.', 'The knowledge was not saved.'), text),
      );
    } finally {
      setPending(false);
    }
  }

  async function issuePairing(): Promise<void> {
    if (!tenantId || !siteId) {
      setError(
        text(
          'Λείπει η ιστοσελίδα. Γυρίστε στο προηγούμενο βήμα.',
          'The website is missing. Return to the previous step.',
        ),
      );
      return;
    }
    setPending(true);
    setError(null);
    try {
      const rows = await readJson<InstallationRow[]>(
        `/v1/sites/${siteId}/installations`,
        undefined,
        tenantId,
      );
      if (rows.some((row) => row.status === 'ACTIVE')) {
        setConnected(true);
        go(step + 1, { pairingCode: '', pairingExpiresAt: '', pairingSiteId: siteId });
        return;
      }
      const issued = await readJson<{ code: string; expiresAt: string }>(
        `/v1/sites/${siteId}/pairing-codes`,
        { method: 'POST' },
        tenantId,
      );
      setPairingCode(issued.code);
      setPairingExpiresAt(issued.expiresAt);
      setPairingSiteId(siteId);
      remember({
        pairingCode: issued.code,
        pairingExpiresAt: issued.expiresAt,
        pairingSiteId: siteId,
      });
    } catch (caught) {
      setError(
        explain(
          caught,
          text('Ο κωδικός σύζευξης δεν εκδόθηκε.', 'The pairing code was not issued.'),
          text,
        ),
      );
    } finally {
      setPending(false);
    }
  }

  async function downloadPlugin(): Promise<void> {
    if (!tenantId || !siteId) return;
    setPending(true);
    setError(null);
    try {
      const response = await apiRequest(`/v1/sites/${siteId}/plugin`, {
        headers: { 'X-Tenant-Id': tenantId },
      });
      if (!response.ok) {
        const body: unknown = await response.json().catch(() => null);
        const message =
          body && typeof body === 'object' && 'error' in body
            ? String((body as { error?: { message?: string } }).error?.message ?? '')
            : '';
        throw new ApiError(
          response.status,
          message || text('Η λήψη απέτυχε.', 'The download failed.'),
        );
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'unibyte-chat.zip';
      link.click();
      URL.revokeObjectURL(url);
    } catch (caught) {
      setError(
        explain(caught, text('Το plugin δεν κατέβηκε.', 'The plugin was not downloaded.'), text),
      );
    } finally {
      setPending(false);
    }
  }

  async function sendInvite(event: FormEvent): Promise<void> {
    event.preventDefault();
    const email = inviteEmail.trim().toLowerCase();
    if (!email.includes('@')) {
      setError(
        text(
          'Συμπληρώστε email διαχειριστή, ή παραλείψτε την πρόσκληση.',
          'Enter an administrator email, or skip the invitation.',
        ),
      );
      return;
    }
    if (!tenantId) {
      setError(text('Λείπει ο πελάτης.', 'The customer is missing.'));
      return;
    }
    setPending(true);
    setError(null);
    try {
      await readJson(
        '/v1/members/invitations',
        { method: 'POST', body: JSON.stringify({ email, role: 'TENANT_ADMIN' }) },
        tenantId,
      );
      setInviteSent(true);
      go(step + 1);
    } catch (caught) {
      setError(
        explain(caught, text('Η πρόσκληση δεν στάλθηκε.', 'The invitation was not sent.'), text),
      );
    } finally {
      setPending(false);
    }
  }

  async function finish(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      await readJson(
        '/v1/platform/application-settings',
        {
          method: 'PATCH',
          body: JSON.stringify({
            items: [{ key: 'setup_wizard_completed', value: 'true' }],
          }),
        },
        null,
      );
      await mutate(
        ['/v1/platform/application-settings', null],
        (cached: unknown) => {
          if (!cached || typeof cached !== 'object' || !('items' in cached)) return cached;
          const body = cached as { items: SettingItem[] };
          const items = body.items.map((item) =>
            item.key === 'setup_wizard_completed' ? { ...item, value: 'true' } : item,
          );
          const hasFlag = items.some((item) => item.key === 'setup_wizard_completed');
          return {
            ...body,
            items: hasFlag ? items : [...items, { key: 'setup_wizard_completed', value: 'true' }],
          };
        },
        { revalidate: true },
      );
      try {
        window.sessionStorage.removeItem(SETUP_DRAFT_KEY);
      } catch {
        // Completion is already stored on the server.
      }
      props.onFinished(tenantId);
    } catch (caught) {
      setError(
        explain(caught, text('Η ολοκλήρωση δεν αποθηκεύτηκε.', 'Completion was not saved.'), text),
      );
      setPending(false);
    }
  }

  if (loading) {
    return (
      <p className="text-sm text-slate-500 dark:text-slate-400">
        {text('Φόρτωση οδηγού…', 'Loading setup wizard…')}
      </p>
    );
  }
  if (loadError) {
    return <ErrorNote message={loadError} />;
  }
  if (alreadyDone) {
    return (
      <Card
        icon="check"
        title={text('Η αρχική ρύθμιση έχει ολοκληρωθεί', 'Initial setup is complete')}
      >
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {text(
            'Η πλατφόρμα έχει ήδη περάσει τον οδηγό πρώτης εκκίνησης. Οι ρυθμίσεις αλλάζουν από τις αντίστοιχες σελίδες.',
            'The platform has already completed the first-run wizard. Settings can be changed on their respective pages.',
          )}
        </p>
        <Button type="button" className="mt-4" onClick={() => props.onFinished(tenantId)}>
          <Icon name="arrow" className="size-4" />
          {text('Μετάβαση στην πλατφόρμα', 'Go to platform')}
        </Button>
      </Card>
    );
  }

  const shownCode = pairingSiteId === siteId ? pairingCode : '';
  const selectedPlan = plans.find((plan) => plan.code === planCode) ?? null;
  const totalSteps = SETUP_STEPS.length;
  const progressPercent = Math.round(((step + 1) / totalSteps) * 100);

  return (
    <section className="mx-auto grid max-w-5xl gap-6 lg:grid-cols-[15rem_minmax(0,1fr)]">
      <div className="admin-surface space-y-3 p-4 lg:p-5">
        <div>
          <p className="text-xs font-medium tracking-wide text-slate-500 uppercase dark:text-slate-400">
            {text('Πρόοδος', 'Progress')} {step + 1} {text('από', 'of')} {totalSteps}
          </p>
          <div
            className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progressPercent}
            aria-label={text(`Πρόοδος ${progressPercent}%`, `Progress ${progressPercent}%`)}
          >
            <div
              className="h-full rounded-full bg-[color:var(--admin-accent,#2563EB)] transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
        <ol
          className="flex gap-2 overflow-x-auto lg:flex-col lg:overflow-visible"
          aria-label={text('Βήματα', 'Steps')}
        >
          {SETUP_STEPS.map((id, index) => {
            const meta = stepMeta[id];
            const active = index === step;
            const past = index < step;
            const blocked = index > step;
            const statusIcon: IconName = past ? 'check' : 'alert';
            return (
              <li key={id}>
                <button
                  type="button"
                  disabled={blocked}
                  aria-current={active ? 'step' : undefined}
                  onClick={() => {
                    if (index < step) go(index);
                  }}
                  className={cx(
                    'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition',
                    active
                      ? 'bg-[color:var(--admin-accent,#2563EB)]/8 font-medium text-slate-900 ring-1 ring-[color:var(--admin-accent,#2563EB)]/25 dark:bg-slate-950 dark:text-slate-100'
                      : 'text-slate-500 dark:text-slate-400',
                    blocked
                      ? 'cursor-default opacity-60'
                      : 'hover:bg-slate-50 hover:text-slate-800 dark:hover:bg-slate-800/60 dark:hover:text-slate-200',
                  )}
                >
                  <span
                    className={cx(
                      'grid size-7 shrink-0 place-items-center rounded-full text-xs',
                      active || past
                        ? 'bg-[color:var(--admin-accent,#2563EB)] text-white'
                        : 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
                    )}
                  >
                    <Icon name={statusIcon} className="size-3.5" />
                  </span>
                  <span className="truncate">{meta.label}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="min-w-0 space-y-4">
        {error ? <ErrorNote message={error} /> : null}
        {notice ? <p className="text-sm text-amber-700 dark:text-amber-300">{notice}</p> : null}

        {current === 'welcome' ? (
          <Card
            icon="home"
            title={text('Ας ετοιμάσουμε την πλατφόρμα', "Let's set up the platform")}
            description={text(
              'Ο οδηγός κάνει τις ρυθμίσεις που χρειάζονται για να απαντάει το chatbot σε μία ιστοσελίδα.',
              'The wizard configures everything the chatbot needs to answer on a website.',
            )}
          >
            <ul className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
              {(
                [
                  ['eye', text('Όνομα και χρώμα του πίνακα.', 'Dashboard name and color.')],
                  [
                    'pulse',
                    text(
                      'Πάροχος απαντήσεων και email προσκλήσεων.',
                      'Answer provider and invitation email.',
                    ),
                  ],
                  [
                    'building',
                    text(
                      'Πελάτης, πακέτο και πρώτη ιστοσελίδα με ενεργό bot.',
                      'Customer, plan, and first website with an active bot.',
                    ),
                  ],
                  [
                    'link',
                    text(
                      'Κείμενο γνώσης, σύζευξη WordPress και πρόσκληση διαχειριστή.',
                      'Knowledge text, WordPress pairing, and administrator invitation.',
                    ),
                  ],
                ] as const
              ).map(([icon, label]) => (
                <li key={icon} className="flex items-center gap-3">
                  <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    <Icon name={icon} className="size-4" />
                  </span>
                  {label}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              {text(
                'Ό,τι υπάρχει ήδη από την εγκατάσταση μπορεί να χρησιμοποιηθεί. Το email, η γνώση, το WordPress και η πρόσκληση μπορούν να μείνουν για αργότερα.',
                'Anything already configured during installation can be reused. Email, knowledge, WordPress, and the invitation can be left for later.',
              )}
            </p>
            <Footer
              pending={pending}
              onLater={props.onLater}
              primary={text('Ξεκίνα', 'Get started')}
              onPrimary={() => go(1)}
            />
          </Card>
        ) : null}

        {current === 'brand' ? (
          <form onSubmit={(event) => void saveBrand(event)}>
            <Card
              icon="eye"
              title={text('Εμφάνιση', 'Appearance')}
              description={text(
                'Φαίνεται στη σύνδεση, στο μενού και στα email.',
                'Shown on the sign-in page, in the menu, and in emails.',
              )}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={text('Όνομα προϊόντος', 'Product name')}>
                  <TextInput
                    required
                    value={productName}
                    onChange={(event) => setProductName(event.target.value)}
                  />
                </Field>
                <Field label={text('Χρώμα έμφασης', 'Accent color')}>
                  <ColorField value={accent} onChange={setAccent} />
                </Field>
              </div>
              <Footer
                pending={pending}
                onLater={props.onLater}
                onBack={() => go(step - 1)}
                primary={text('Αποθήκευση και συνέχεια', 'Save and continue')}
                secondary={text('Παράλειψη', 'Skip')}
                onSecondary={() => go(step + 1)}
              />
            </Card>
          </form>
        ) : null}

        {current === 'ai' ? (
          <form onSubmit={(event) => void saveProvider(event)}>
            <Card
              icon="pulse"
              title={text('Πάροχος AI', 'AI provider')}
              description={text(
                'Ισχύει για όλους τους πελάτες. Το κλειδί μένει στον διακομιστή.',
                'Applies to all customers. The key remains on the server.',
              )}
            >
              <Field label={text('Πάροχος', 'Provider')}>
                <Select
                  className="mt-1.5"
                  value={providerKind}
                  onChange={(next) => setProviderKind(next === 'openai' ? 'openai' : 'mock')}
                  options={[
                    {
                      value: 'mock',
                      label: text(
                        'Mock — τοπικές απαντήσεις, χωρίς χρέωση',
                        'Mock — local answers, no charge',
                      ),
                    },
                    {
                      value: 'openai',
                      label: text('OpenAI — πραγματικές απαντήσεις', 'OpenAI — real answers'),
                    },
                  ]}
                />
              </Field>
              <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">
                {providerKind === 'openai'
                  ? providerReady
                    ? text(
                        `Ο διακομιστής έχει κλειδί και μοντέλο ${providerModel ?? ''}. Οι κλήσεις μπορεί να χρεώνονται.`,
                        `The server has a key and model ${providerModel ?? ''}. Calls may incur charges.`,
                      )
                    : text(
                        'Ο διακομιστής δεν έχει ακόμη κλειδί ή μοντέλο. Ορίστε τα στο περιβάλλον του API και ξανανοίξτε αυτό το βήμα. Μέχρι τότε μείνετε στο Mock.',
                        'The server does not have a key or model yet. Configure them in the API environment and reopen this step. Until then, keep using Mock.',
                      )
                  : text(
                      'Οι απαντήσεις παράγονται τοπικά και το εκτιμώμενο κόστος μένει μηδέν. Κατάλληλο για δοκιμή.',
                      'Answers are generated locally and estimated cost remains zero. Suitable for testing.',
                    )}
              </p>
              <Footer
                pending={pending}
                onLater={props.onLater}
                onBack={() => go(step - 1)}
                primary={text('Αποθήκευση και συνέχεια', 'Save and continue')}
                secondary={text('Παράλειψη', 'Skip')}
                onSecondary={() => go(step + 1)}
              />
            </Card>
          </form>
        ) : null}

        {current === 'mail' ? (
          <form onSubmit={(event) => void saveSmtp(event)}>
            <Card
              icon="mail"
              title="Email"
              description={text(
                'Χρειάζεται για προσκλήσεις και επαναφορά κωδικού. Χωρίς SMTP τα μηνύματα μένουν στο τοπικό αρχείο δοκιμών.',
                'Required for invitations and password resets. Without SMTP, messages remain in the local test file.',
              )}
            >
              {smtpSource !== 'none' ? (
                <p className="mb-4 text-sm text-emerald-700 dark:text-emerald-300">
                  {text('Υπάρχει ήδη ρύθμιση email (', 'Email is already configured (')}
                  {smtpSource === 'env'
                    ? text('από το περιβάλλον', 'from the environment')
                    : text('αποθηκευμένη εδώ', 'saved here')}
                  ).
                </p>
              ) : null}
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Host">
                  <TextInput
                    value={smtpHost}
                    onChange={(event) => setSmtpHost(event.target.value)}
                  />
                </Field>
                <Field label="Port">
                  <TextInput
                    type="number"
                    min={1}
                    max={65535}
                    value={smtpPort}
                    onChange={(event) => setSmtpPort(event.target.value)}
                  />
                </Field>
                <Field
                  label={text('Χρήστης', 'Username')}
                  hint={text(
                    'Κενό αν ο διακομιστής δεν ζητά αυθεντικοποίηση.',
                    'Leave blank if the server does not require authentication.',
                  )}
                >
                  <TextInput
                    value={smtpUser}
                    onChange={(event) => setSmtpUser(event.target.value)}
                  />
                </Field>
                <Field label={text('Αποστολέας', 'Sender')}>
                  <TextInput
                    type="email"
                    value={smtpFrom}
                    onChange={(event) => setSmtpFrom(event.target.value)}
                  />
                </Field>
                <Field
                  label={text('Κωδικός', 'Password')}
                  hint={text(
                    'Δεν εμφανίζεται ξανά μετά την αποθήκευση.',
                    'It is not shown again after saving.',
                  )}
                >
                  <TextInput
                    type="password"
                    autoComplete="new-password"
                    value={smtpPassword}
                    onChange={(event) => setSmtpPassword(event.target.value)}
                  />
                </Field>
              </div>
              <Footer
                pending={pending}
                onLater={props.onLater}
                onBack={() => go(step - 1)}
                primary={
                  smtpSource !== 'none' && !smtpHost.trim()
                    ? text('Συνέχεια', 'Continue')
                    : text('Αποθήκευση και συνέχεια', 'Save and continue')
                }
                onPrimary={
                  smtpSource !== 'none' && !smtpHost.trim() ? () => go(step + 1) : undefined
                }
                secondary={text('Παράλειψη', 'Skip')}
                onSecondary={() => go(step + 1)}
              />
            </Card>
          </form>
        ) : null}

        {current === 'customer' ? (
          <form onSubmit={(event) => void saveCustomer(event)}>
            <Card
              icon="building"
              title={text('Πελάτης και πακέτο', 'Customer and plan')}
              description={text(
                'Χωρίς ενεργό πακέτο δεν δημιουργείται ιστοσελίδα. Τιμή 0 δεν εκδίδει τιμολόγιο.',
                'A website cannot be created without an active plan. A price of 0 does not issue an invoice.',
              )}
            >
              <Choice
                label={text('Πελάτης', 'Customer')}
                value={customerMode}
                onChange={setCustomerMode}
                options={[
                  {
                    value: 'new',
                    label: text('Νέος πελάτης', 'New customer'),
                    icon: 'plus',
                  },
                  {
                    value: 'existing',
                    label: text('Υπάρχων', 'Existing'),
                    icon: 'building',
                    disabled: tenants.length === 0,
                  },
                ]}
              />
              {customerMode === 'new' ? (
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <Field label={text('Επωνυμία', 'Company name')}>
                    <TextInput
                      required
                      value={tenantName}
                      onChange={(event) => setTenantName(event.target.value)}
                      placeholder={text('π.χ. Example Hotels', 'e.g. Example Hotels')}
                    />
                  </Field>
                  <Field label={text('Ζώνη ώρας', 'Time zone')}>
                    <Select
                      className="mt-1.5"
                      value={timezone}
                      onChange={setTimezone}
                      searchable
                      options={timezoneChoices(timezone).map((zone) => ({
                        value: zone,
                        label: zone,
                      }))}
                    />
                  </Field>
                  <div className="sm:col-span-2">
                    <TenantBillingFields values={billing} onChange={setBilling} />
                  </div>
                </div>
              ) : (
                <Field label={text('Πελάτης', 'Customer')}>
                  <Select
                    className="mt-1.5"
                    value={tenantId}
                    onChange={setTenantId}
                    options={tenants.map((tenant) => ({ value: tenant.id, label: tenant.name }))}
                  />
                </Field>
              )}
              <div className="mt-6">
                <Choice
                  label={text('Πακέτο', 'Plan')}
                  value={planMode}
                  onChange={setPlanMode}
                  options={[
                    {
                      value: 'existing',
                      label: text('Υπάρχον', 'Existing'),
                      icon: 'layers',
                      disabled: plans.length === 0,
                    },
                    {
                      value: 'new',
                      label: text('Νέο πακέτο', 'New plan'),
                      icon: 'plus',
                    },
                  ]}
                />
              </div>
              {planMode === 'existing' ? (
                <Field label={text('Πακέτο', 'Plan')}>
                  <Select
                    className="mt-1.5"
                    value={planCode}
                    onChange={setPlanCode}
                    options={plans.map((plan) => ({
                      value: plan.code,
                      label: `${plan.name} · ${formatMoney(plan.monthlyPriceCents, locale)}`,
                    }))}
                  />
                </Field>
              ) : (
                <div className="mt-4 grid gap-4 sm:grid-cols-3">
                  <Field label={text('Όνομα', 'Name')}>
                    <TextInput
                      required
                      value={newPlanName}
                      onChange={(event) => {
                        setNewPlanName(event.target.value);
                        setNewPlanCode(planCodeFromName(event.target.value));
                      }}
                    />
                  </Field>
                  <Field
                    label={text('Κωδικός', 'Code')}
                    hint={text(
                      'Πεζά λατινικά, αριθμοί, παύλα.',
                      'Lowercase Latin letters, numbers, and hyphens.',
                    )}
                  >
                    <TextInput
                      required
                      value={newPlanCode}
                      onChange={(event) => setNewPlanCode(event.target.value)}
                    />
                  </Field>
                  <Field label={text('Τιμή ευρώ / μήνα', 'Price in euros / month')}>
                    <TextInput
                      inputMode="decimal"
                      value={priceEuros}
                      onChange={(event) => setPriceEuros(event.target.value)}
                    />
                  </Field>
                </div>
              )}
              {customerMode === 'existing' &&
              activePlanCode &&
              activePlanCode === (planMode === 'existing' ? planCode : newPlanCode) ? (
                <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
                  {text(
                    `Ο πελάτης έχει ήδη το πακέτο ${selectedPlan?.name ?? activePlanCode}. Δεν θα γίνει νέα ανάθεση.`,
                    `The customer already has the ${selectedPlan?.name ?? activePlanCode} plan. It will not be assigned again.`,
                  )}
                </p>
              ) : null}
              <Footer
                pending={pending}
                onLater={props.onLater}
                onBack={() => go(step - 1)}
                primary={text('Συνέχεια', 'Continue')}
                secondary={text('Παράλειψη', 'Skip')}
                onSecondary={() => go(step + 1)}
              />
            </Card>
          </form>
        ) : null}

        {current === 'site' ? (
          <form onSubmit={(event) => void saveSite(event)}>
            <Card
              icon="globe"
              title={text('Ιστοσελίδα και bot', 'Website and bot')}
              description={text(
                'Το origin πρέπει να ταιριάζει ακριβώς με τον browser του επισκέπτη. Το bot ενεργοποιείται σε αυτό το βήμα.',
                "The origin must exactly match the visitor's browser origin. The bot is enabled in this step.",
              )}
            >
              <Choice
                label={text('Ιστοσελίδα', 'Website')}
                value={siteMode}
                onChange={setSiteMode}
                options={[
                  {
                    value: 'new',
                    label: text('Νέα ιστοσελίδα', 'New website'),
                    icon: 'plus',
                  },
                  {
                    value: 'existing',
                    label: text('Υπάρχουσα', 'Existing'),
                    icon: 'globe',
                    disabled: sites.length === 0,
                  },
                ]}
              />
              {siteMode === 'new' ? (
                <div className="mt-4 grid gap-4">
                  <Field label={text('Όνομα', 'Name')}>
                    <TextInput
                      required
                      value={siteName}
                      onChange={(event) => setSiteName(event.target.value)}
                      placeholder={text('π.χ. Κεντρικό site', 'e.g. Main site')}
                    />
                  </Field>
                  <Field
                    label="Origin"
                    hint={text(
                      'Χωρίς διαδρομή. Παράδειγμα: https://www.example.com',
                      'No path. Example: https://www.example.com',
                    )}
                  >
                    <TextInput
                      required
                      value={origin}
                      onChange={(event) => setOrigin(event.target.value)}
                    />
                  </Field>
                </div>
              ) : (
                <Field label={text('Ιστοσελίδα', 'Website')}>
                  <Select
                    className="mt-1.5"
                    value={siteId}
                    onChange={setSiteId}
                    options={sites.map((site) => ({
                      value: site.id,
                      label: `${site.name} · ${site.canonicalOrigin}`,
                    }))}
                  />
                </Field>
              )}
              <Field
                label={text('Χαιρετισμός', 'Greeting')}
                hint={text(
                  'Προαιρετικό. Αν μείνει κενό, κρατιέται ο προεπιλεγμένος.',
                  'Optional. If left blank, the default is kept.',
                )}
              >
                <TextInput
                  value={greeting}
                  onChange={(event) => setGreeting(event.target.value)}
                  placeholder={text('Γεια σας. Πώς μπορώ να βοηθήσω;', 'Hello. How can I help?')}
                />
              </Field>
              <Footer
                pending={pending}
                onLater={props.onLater}
                onBack={() => go(step - 1)}
                primary={text('Ενεργοποίηση και συνέχεια', 'Enable and continue')}
                secondary={text('Παράλειψη', 'Skip')}
                onSecondary={() => go(step + 1)}
              />
            </Card>
          </form>
        ) : null}

        {current === 'knowledge' ? (
          <form onSubmit={(event) => void saveKnowledge(event)}>
            <Card
              icon="file"
              title={text('Πρώτη γνώση', 'Initial knowledge')}
              description={text(
                'Το bot απαντά μόνο από εγκεκριμένο κείμενο. Μπορείτε να βάλετε μια σύντομη παράγραφο τώρα ή να περιμένετε τον συγχρονισμό από το WordPress.',
                'The bot answers only from approved text. You can add a short paragraph now or wait for WordPress synchronization.',
              )}
            >
              <div className="grid gap-4">
                <Field label={text('Τίτλος', 'Title')}>
                  <TextInput
                    value={knowledgeTitle}
                    onChange={(event) => setKnowledgeTitle(event.target.value)}
                  />
                </Field>
                <Field label={text('Κείμενο', 'Text')}>
                  <Textarea
                    rows={6}
                    value={knowledgeText}
                    onChange={(event) => setKnowledgeText(event.target.value)}
                    placeholder={text(
                      'Ώρες λειτουργίας, διεύθυνση, τι περιλαμβάνει η υπηρεσία…',
                      'Opening hours, address, what the service includes…',
                    )}
                  />
                </Field>
              </div>
              <Footer
                pending={pending}
                onLater={props.onLater}
                onBack={() => go(step - 1)}
                primary={text('Αποθήκευση και συνέχεια', 'Save and continue')}
                secondary={text('Παράλειψη', 'Skip')}
                onSecondary={() => go(step + 1)}
              />
            </Card>
          </form>
        ) : null}

        {current === 'connect' ? (
          <Card
            icon="link"
            title={text('Σύνδεση WordPress', 'Connect WordPress')}
            description={text(
              'Το plugin στέλνει το περιεχόμενο του site. Ο κωδικός σύζευξης φαίνεται μία φορά και λήγει σε λίγα λεπτά.',
              'The plugin sends the site content. The pairing code is shown once and expires in a few minutes.',
            )}
          >
            {connected ? (
              <p className="text-sm text-emerald-700 dark:text-emerald-300">
                {text(
                  'Αυτή η ιστοσελίδα έχει ήδη ενεργή εγκατάσταση.',
                  'This website already has an active installation.',
                )}
              </p>
            ) : shownCode ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  API:{' '}
                  <span className="font-medium text-slate-900 dark:text-slate-100">
                    {getApiBaseUrl()}
                  </span>
                </p>
                <p className="break-all rounded-lg bg-slate-100 px-3 py-2 font-mono text-sm text-slate-900 dark:bg-slate-800 dark:text-slate-100">
                  {shownCode}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {text('Λήγει', 'Expires')}{' '}
                  {pairingExpiresAt
                    ? new Date(pairingExpiresAt).toLocaleString(locale === 'en' ? 'en-GB' : 'el-GR')
                    : text('σύντομα', 'soon')}
                  .
                </p>
                <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-700 dark:text-slate-300">
                  <li>
                    {text(
                      'Κατεβάστε το plugin ZIP και ανεβάστε το στο WordPress (Πρόσθετα → Μεταφόρτωση).',
                      'Download the plugin ZIP and upload it to WordPress (Plugins → Upload Plugin).',
                    )}
                  </li>
                  <li>
                    {text(
                      'Ενεργοποιήστε το και ανοίξτε τη σελίδα σύνδεσης του plugin.',
                      'Activate it and open the plugin connection page.',
                    )}
                  </li>
                  <li>
                    {text(
                      'Επικολλήστε το API URL και τον κωδικό σύζευξης πριν λήξει.',
                      'Paste the API URL and pairing code before it expires.',
                    )}
                  </li>
                  <li>
                    {text(
                      'Επιλέξτε δημοσιευμένες σελίδες και πατήστε συγχρονισμό.',
                      'Select published pages and start synchronization.',
                    )}
                  </li>
                </ol>
              </div>
            ) : (
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {text(
                  'Εκδώστε κωδικό όταν είστε έτοιμοι να τον επικολλήσετε στο WordPress.',
                  'Issue a code when you are ready to paste it into WordPress.',
                )}
              </p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={pending || !siteId}
                onClick={() => void downloadPlugin()}
              >
                <Icon name="download" className="size-4" />
                {text('Λήψη plugin', 'Download plugin')}
              </Button>
              {shownCode ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    void navigator.clipboard.writeText(shownCode).catch(() => undefined)
                  }
                >
                  <Icon name="copy" className="size-4" />
                  {text('Αντιγραφή κωδικού', 'Copy code')}
                </Button>
              ) : null}
            </div>
            <Footer
              pending={pending}
              onLater={props.onLater}
              onBack={() => go(step - 1)}
              primary={
                connected || shownCode
                  ? text('Συνέχεια', 'Continue')
                  : text('Έκδοση κωδικού', 'Issue code')
              }
              onPrimary={connected || shownCode ? () => go(step + 1) : () => void issuePairing()}
              secondary={text('Παράλειψη', 'Skip')}
              onSecondary={() => go(step + 1)}
            />
          </Card>
        ) : null}

        {current === 'invite' ? (
          <form onSubmit={(event) => void sendInvite(event)}>
            <Card
              icon="users"
              title={text('Διαχειριστής πελάτη', 'Customer administrator')}
              description={text(
                'Η πρόσκληση ανοίγει λογαριασμό μόνο για αυτόν τον πελάτη. Εσείς παραμένετε διαχειριστής της πλατφόρμας.',
                'The invitation creates an account only for this customer. You remain the platform administrator.',
              )}
            >
              <Field
                label="Email"
                hint={text(
                  'Χωρίς SMTP το μήνυμα γράφεται στο τοπικό αρχείο δοκιμών.',
                  'Without SMTP, the message is written to the local test file.',
                )}
              >
                <TextInput
                  type="email"
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  placeholder="admin@example.com"
                />
              </Field>
              <Footer
                pending={pending}
                onLater={props.onLater}
                onBack={() => go(step - 1)}
                primary={text('Αποστολή και συνέχεια', 'Send and continue')}
                secondary={text('Παράλειψη', 'Skip')}
                onSecondary={() => go(step + 1)}
              />
            </Card>
          </form>
        ) : null}

        {current === 'done' ? (
          <Card
            icon="check"
            title={text(
              'Η πλατφόρμα είναι έτοιμη για την πρώτη ιστοσελίδα',
              'The platform is ready for the first website',
            )}
          >
            <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
              <CheckLine
                ok
                label={text(
                  `Πάροχος: ${providerKind === 'openai' ? 'OpenAI' : 'Mock'}`,
                  `Provider: ${providerKind === 'openai' ? 'OpenAI' : 'Mock'}`,
                )}
              />
              <CheckLine
                ok={smtpSource !== 'none'}
                label={
                  smtpSource !== 'none'
                    ? text('Το email είναι ρυθμισμένο.', 'Email is configured.')
                    : text(
                        'Το email δεν ρυθμίστηκε. Οι προσκλήσεις μένουν τοπικά.',
                        'Email was not configured. Invitations remain local.',
                      )
                }
              />
              <CheckLine
                ok={Boolean(tenantId)}
                label={text('Υπάρχει πελάτης με πακέτο.', 'A customer has a plan.')}
              />
              <CheckLine
                ok={Boolean(siteId)}
                label={text('Η ιστοσελίδα έχει ενεργό bot.', 'The website has an active bot.')}
              />
              <CheckLine
                ok={knowledgeReady}
                label={
                  knowledgeReady
                    ? text(
                        'Η πρώτη πηγή στάλθηκε για ενεργοποίηση.',
                        'The first source was submitted for activation.',
                      )
                    : text(
                        'Δεν προστέθηκε γνώση. Το bot απαντά αφού εγκριθεί περιεχόμενο.',
                        'No knowledge was added. The bot answers after content is approved.',
                      )
                }
              />
              <CheckLine
                ok={connected || Boolean(shownCode)}
                label={
                  connected
                    ? text('Το WordPress είναι ήδη συνδεδεμένο.', 'WordPress is already connected.')
                    : shownCode
                      ? text(
                          'Ο κωδικός σύζευξης εκδόθηκε. Χρησιμοποιήστε τον πριν λήξει.',
                          'The pairing code was issued. Use it before it expires.',
                        )
                      : text(
                          'Το WordPress δεν συνδέθηκε ακόμη.',
                          'WordPress has not been connected yet.',
                        )
                }
              />
              <CheckLine
                ok={inviteSent}
                label={
                  inviteSent
                    ? text('Η πρόσκληση στάλθηκε.', 'The invitation was sent.')
                    : text(
                        'Δεν στάλθηκε πρόσκληση διαχειριστή.',
                        'No administrator invitation was sent.',
                      )
                }
              />
            </ul>
            <Footer
              pending={pending}
              onLater={props.onLater}
              onBack={() => go(step - 1)}
              primary={text('Ολοκλήρωση', 'Finish')}
              onPrimary={() => void finish()}
            />
          </Card>
        ) : null}
      </div>
    </section>
  );
}

function CheckLine(props: { ok: boolean; label: string }) {
  return (
    <li className="flex items-start gap-2">
      <Icon
        name={props.ok ? 'check' : 'alert'}
        className={cx('mt-0.5 size-4 shrink-0', props.ok ? 'text-emerald-600' : 'text-amber-600')}
      />
      <span>{props.label}</span>
    </li>
  );
}

function Choice<T extends string>(props: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<{ value: T; label: string; icon?: IconName; disabled?: boolean }>;
}) {
  return (
    <div>
      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{props.label}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {props.options.map((option) => (
          <button
            key={option.value}
            type="button"
            disabled={option.disabled}
            onClick={() => props.onChange(option.value)}
            className={cx(
              'inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm disabled:opacity-40',
              props.value === option.value
                ? 'bg-[color:var(--admin-accent,#2563EB)] text-white'
                : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
            )}
          >
            {option.icon ? <Icon name={option.icon} className="size-4" /> : null}
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function primaryIcon(label: string): IconName {
  if (label.startsWith('Αποθήκευση') || label.startsWith('Save')) return 'save';
  if (
    label.startsWith('Ενεργοποίηση') ||
    label.startsWith('Enable') ||
    label === 'Ολοκλήρωση' ||
    label === 'Finish'
  )
    return 'check';
  if (label.startsWith('Έκδοση') || label.startsWith('Issue')) return 'key';
  if (label.startsWith('Αποστολή') || label.startsWith('Send')) return 'mail';
  return 'arrow';
}

function Footer(props: {
  pending: boolean;
  primary: string;
  onPrimary?: (() => void) | undefined;
  onBack?: (() => void) | undefined;
  onLater?: (() => void) | undefined;
  secondary?: string | undefined;
  onSecondary?: (() => void) | undefined;
}) {
  const { text } = useAdminLocale();
  const submit = props.onPrimary === undefined;
  return (
    <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap gap-2">
        {props.onBack ? (
          <Button type="button" variant="ghost" disabled={props.pending} onClick={props.onBack}>
            <Icon name="chevron-left" className="size-4" />
            {text('Πίσω', 'Back')}
          </Button>
        ) : null}
        {props.onLater ? (
          <Button type="button" variant="ghost" disabled={props.pending} onClick={props.onLater}>
            <Icon name="clock" className="size-4" />
            {text('Αργότερα', 'Later')}
          </Button>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-2">
        {props.secondary && props.onSecondary ? (
          <Button
            type="button"
            variant="secondary"
            disabled={props.pending}
            onClick={props.onSecondary}
          >
            <Icon name="chevron-right" className="size-4" />
            {props.secondary}
          </Button>
        ) : null}
        <Button
          type={submit ? 'submit' : 'button'}
          disabled={props.pending}
          onClick={props.onPrimary}
        >
          <Icon name={props.pending ? 'refresh' : primaryIcon(props.primary)} className="size-4" />
          {props.pending ? text('Αποθήκευση…', 'Saving…') : props.primary}
        </Button>
      </div>
    </div>
  );
}
